import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";
import { useApp } from "../contexts/AppContext";
import { ArrowRight, Play, ChevronLeft, ChevronRight } from "lucide-react";
import EditorialBanners from "../components/EditorialBanners";
import { listBlogPosts } from "../services/blog";

export default function HomePage() {
  const { t, content, lang, tp } = useApp();
  const [products, setProducts] = useState([]);
  const [artists, setArtists] = useState([]);
  const [categories, setCategories] = useState([]);
  const [slide, setSlide] = useState(0);
  // Journal section is admin-driven: starts empty. If admin hasn't published
  // any blog posts, the whole "Oxumalı" section is hidden on the homepage.
  const [journal, setJournal] = useState([]);

  useEffect(() => {
    api.get("/products?limit=24").then((r) => setProducts(r.data)).catch(() => setProducts([]));
    api.get("/artists").then((r) => setArtists(r.data)).catch(() => setArtists([]));
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => setCategories([]));
    listBlogPosts({ onlyPublished: true }).then((items) => {
      if (items && items.length) setJournal(items.slice(0, 3));
      else setJournal([]);
    }).catch(() => setJournal([]));
  }, []);

  const pick = (v) => {
    if (v == null) return "";
    if (typeof v === "string") return v;
    return v[lang] || v.en || v.az || Object.values(v)[0] || "";
  };

  const banners = React.useMemo(() => {
    const fromAdmin = Array.isArray(content?.banners)
      ? content.banners.filter((b) => b?.image_url || b?.video_url || b?.video_url_mobile)
      : [];
    return fromAdmin.length > 0 ? fromAdmin : [];
  }, [content]);

  useEffect(() => {
    if (banners.length <= 1) return;
    const id = setInterval(() => setSlide((s) => (s + 1) % banners.length), 6500);
    return () => clearInterval(id);
  }, [banners.length]);

  const prevSlide = () => setSlide((s) => (s - 1 + banners.length) % banners.length);
  const nextSlide = () => setSlide((s) => (s + 1) % banners.length);

  const artistById = Object.fromEntries(artists.map((a) => [a.id, a]));
  const flagged = products.filter((p) => p.new_arrival);
  const newArrivals = (flagged.length >= 4 ? flagged : products).slice(0, 4);
  const featuredOnly = products.filter((p) => p.featured);
  const bestSellers = (featuredOnly.length >= 4
    ? featuredOnly
    : [...products].sort((a, b) => (b.price_azn || 0) - (a.price_azn || 0))
  ).slice(0, 4);

  return (
    <div className="bg-[var(--bg)] text-[var(--text)] min-h-screen" data-testid="home-page">
      {/* ===================== HERO BANNER — VIDEO ONLY, NO TEXT ===================== */}
      {banners.length > 0 && (
        <section
          className="relative w-full h-screen min-h-[560px] overflow-hidden bg-black -mt-16 md:-mt-20"
          data-testid="hero-banner-section"
        >
          {banners.map((b, i) => {
            const mobileSrc = b.video_url_mobile || b.video_url;
            const desktopSrc = b.video_url || b.video_url_mobile;
            return (
              <div
                key={b.id || i}
                className={`absolute inset-0 transition-opacity duration-[1100ms] ease-out ${i === slide ? "opacity-100 z-10" : "opacity-0 z-0"}`}
                aria-hidden={i !== slide}
              >
                {desktopSrc || mobileSrc ? (
                  <>
                    {/* Desktop video */}
                    {desktopSrc && (
                      <video
                        src={desktopSrc}
                        autoPlay
                        muted
                        loop
                        playsInline
                        preload="auto"
                        className="absolute inset-0 w-full h-full object-cover hidden md:block"
                        data-testid={i === slide ? "hero-video-desktop" : undefined}
                      />
                    )}
                    {/* Mobile video */}
                    {mobileSrc && (
                      <video
                        src={mobileSrc}
                        autoPlay
                        muted
                        loop
                        playsInline
                        preload="auto"
                        className="absolute inset-0 w-full h-full object-cover md:hidden"
                        data-testid={i === slide ? "hero-video-mobile" : undefined}
                      />
                    )}
                  </>
                ) : (
                  <img
                    src={b.image_url}
                    alt={`Banner ${i + 1}`}
                    className={`absolute inset-0 w-full h-full object-cover ${i === slide ? "kenburns-on" : ""}`}
                  />
                )}
              </div>
            );
          })}

          {banners.length > 1 && (
            <>
              <button
                onClick={prevSlide}
                aria-label="Previous"
                className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-11 md:h-11 flex items-center justify-center border border-white/40 text-white hover:bg-[var(--accent)] hover:border-[var(--accent)] transition rounded-full"
                data-testid="hero-prev"              >
                <ChevronLeft size={20} />
              </button>
              <button
                onClick={nextSlide}
                aria-label="Next"
                className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-11 md:h-11 flex items-center justify-center border border-white/40 text-white hover:bg-[var(--accent)] hover:border-[var(--accent)] transition rounded-full"
                data-testid="hero-next"
              >
                <ChevronRight size={20} />
              </button>
              <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-2">
                {banners.map((_, i) => (
                  <button
                    key={i}
                    aria-label={`Slide ${i + 1}`}
                    onClick={() => setSlide(i)}
                    className={`h-[3px] transition-all ${i === slide ? "w-10 bg-white" : "w-5 bg-white/40 hover:bg-white/70"}`}
                    data-testid={`hero-dot-${i}`}
                  />
                ))}
              </div>
            </>
          )}

          {/* MOBILE SCROLL HINT — animated arrow + label */}
          <div
            className="md:hidden absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2 pointer-events-none"
            data-testid="hero-scroll-hint"
          >
            <span className="text-[10px] tracking-[0.32em] uppercase text-white/85 font-light">
              {t.hero?.scrollDown || "Aşağı sürüşdür"}
            </span>
            <span className="scroll-arrow-anim relative w-[1px] h-9 bg-white/60 overflow-hidden">
              <span className="scroll-arrow-anim__head absolute left-1/2 -translate-x-1/2 -bottom-1 w-2 h-2 border-r border-b border-white rotate-45" />
            </span>
          </div>
        </section>
      )}

      {/* ============== MAGAZINE MASTHEAD ============== */}
      {(() => {
        // All masthead strings are admin-editable. If the admin hasn't typed
        // anything for a given field, NOTHING is rendered for it. If every
        // field is empty, the whole masthead section is omitted from the
        // page (no leftover border / spacing).
        const get = (key) => {
          const saved = content?.pages?.home_masthead?.[key];
          if (saved && typeof saved === "object") {
            const v = (saved[lang] || "").trim();
            return v || "";
          }
          if (typeof saved === "string" && saved.trim()) return saved.trim();
          return "";
        };
        const volume = get("masthead_volume");
        const date   = get("masthead_date");
        const label  = get("masthead_label");
        const cta    = get("masthead_cta");
        const marqueeRaw = get("masthead_marquee");
        const marqueeItems = marqueeRaw
          ? marqueeRaw.split(/\s*·\s*/).map((s) => s.trim()).filter(Boolean)
          : [];
        const anyVisible = !!(volume || date || label || cta || marqueeItems.length);
        if (!anyVisible) return null;
        return (
        <section className="border-y border-[var(--border-soft)] bg-[var(--bg)]" data-testid="masthead">
          <div className="max-w-screen-2xl mx-auto px-5 md:px-12 py-5 md:py-6 flex items-center gap-6 md:gap-10 flex-wrap">
            {volume && (
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-[var(--accent)] animate-pulse"/>
                <span className="metadata text-[var(--text)]">{volume}</span>
              </div>
            )}
            {date && <>
              {volume && <span className="hidden sm:inline metadata text-[var(--text-muted)]">·</span>}
              <span className="metadata text-[var(--text-muted)]">{date}</span>
            </>}
            {label && <>
              {(volume || date) && <span className="hidden sm:inline metadata text-[var(--text-muted)]">·</span>}
              <span className="metadata text-[var(--text-muted)]">{label}</span>
            </>}
            {marqueeItems.length > 0 && (
              <div className="flex-1 hidden md:block overflow-hidden">
                <div className="flex gap-10 marquee whitespace-nowrap metadata text-[var(--text-muted)]">
                  {[...Array(2)].flatMap((_, k) => {
                    const seq = [];
                    marqueeItems.forEach((s, i) => {
                      seq.push(<span key={`${k}-w-${i}`}>{s}</span>);
                      if (i < marqueeItems.length - 1) seq.push(<span key={`${k}-d-${i}`} className="text-[var(--accent)]">·</span>);
                    });
                    return seq;
                  })}
                </div>
              </div>
            )}
            {cta && (
              <Link to="/catalog" className="metadata draw-underline text-[var(--text)] hover:text-[var(--accent)] hidden md:inline-flex items-center gap-2 ml-auto">
                {cta} <ArrowRight size={11}/>
              </Link>
            )}
          </div>
        </section>
        );
      })()}

      {/* ============== YENİ GƏLƏNLƏR — minimalist product grid ============== */}
      {newArrivals.length >= 1 && (() => {
        // Strict per-language eyebrow: empty in current lang = hide line+text.
        const savedE = content?.pages?.home?.new_arrivals_eyebrow;
        const eyebrow = (savedE && typeof savedE === "object")
          ? (savedE[lang] ?? "")
          : (t.sections.newArrivalsSub || "");
        // Strict per-language title too — never fall back to another language
        // so AZ visitors never see an English title left in the admin form.
        const savedT = content?.pages?.home?.new_arrivals_title;
        const title = (savedT && typeof savedT === "object")
          ? (savedT[lang] || t.sections.newArrivals)
          : t.sections.newArrivals;
        return (
        <section className="border-t border-[var(--border-soft)]" data-testid="new-arrivals-section">
          <div className="max-w-screen-2xl mx-auto px-5 md:px-12 pt-20 md:pt-24 pb-16 md:pb-20">
            <div className="flex items-end justify-between gap-6 mb-12 md:mb-14 flex-wrap">
              <div>
                {eyebrow && (
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-px bg-[var(--accent)]"/>
                    <span className="section-eyebrow">{eyebrow}</span>
                  </div>
                )}
                {title && (
                  <h2 className="section-title text-4xl md:text-5xl lg:text-[56px] leading-[1.04]">
                    {title}
                  </h2>
                )}
              </div>
              <Link to="/catalog?sort=new" className="metadata draw-underline hover:text-[var(--accent)] inline-flex items-center gap-2" data-testid="see-all-new">
                {t.sections.seeAll} <ArrowRight size={12}/>
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 md:gap-x-8 gap-y-12">
              {newArrivals.slice(0, 4).map((p) => (
                <ProductCard key={p.id} product={p} artistName={artistById[p.artist_id]?.name} artistSlug={artistById[p.artist_id]?.slug} artistId={p.artist_id} />
              ))}
            </div>
          </div>
        </section>
        );
      })()}

      {/* ============== ƏN ÇOX SATILANLAR — minimalist product grid ============== */}
      {bestSellers.length >= 1 && (() => {
        // Strict per-language eyebrow: empty in current lang = hide line+text.
        const savedE = content?.pages?.home?.best_sellers_eyebrow;
        const eyebrow = (savedE && typeof savedE === "object")
          ? (savedE[lang] ?? "")
          : (t.sections.bestSellersSub || "");
        // Strict per-language title — when AZ is selected, never fall back to
        // an English value that might be left over in the admin form.
        const savedT = content?.pages?.home?.best_sellers_title;
        const title = (savedT && typeof savedT === "object")
          ? (savedT[lang] || t.sections.bestSellers)
          : t.sections.bestSellers;
        return (
        <section className="border-t border-[var(--border-soft)] bg-[var(--surface)]" data-testid="best-sellers-section">
          <div className="max-w-screen-2xl mx-auto px-5 md:px-12 pt-20 md:pt-24 pb-20 md:pb-24">
            <div className="flex items-end justify-between gap-6 mb-12 md:mb-14 flex-wrap">
              <div>
                {eyebrow && (
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-px bg-[var(--accent)]"/>
                    <span className="section-eyebrow">{eyebrow}</span>
                  </div>
                )}
                {title && (
                  <h2 className="section-title text-4xl md:text-5xl lg:text-[56px] leading-[1.04]">
                    {title}
                  </h2>
                )}
              </div>
              <Link to="/catalog?sort=popular" className="metadata draw-underline hover:text-[var(--accent)] inline-flex items-center gap-2" data-testid="see-all-best">
                {t.sections.seeAll} <ArrowRight size={12}/>
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 md:gap-x-8 gap-y-12">
              {bestSellers.slice(0, 4).map((p) => (
                <ProductCard key={`best-${p.id}`} product={p} artistName={artistById[p.artist_id]?.name} artistSlug={artistById[p.artist_id]?.slug} artistId={p.artist_id} />
              ))}
            </div>
          </div>
        </section>
        );
      })()}

      {/* ============== EDITORIAL BANNERS (alternating left/right) ============== */}
      <EditorialBanners items={content?.editorial_blocks} t={t} lang={lang} />

      {/* ============== GENRE INDEX (removed) ============== */}

      {/* ============== EDITOR'S PICK / ALBUM SPOTLIGHT (removed) ============== */}

      {/* ============== CURATED COLLECTIONS (removed) ============== */}

      {/* ============== QUOTE (removed) ============== */}

      {/* ============== FEATURED ARTIST ============== */}
      {(() => {
        const featuredId = content?.featured_artist_id;
        const discoIds = Array.isArray(content?.discography_artist_ids) ? content.discography_artist_ids : [];
        const featured = (featuredId && artistById[featuredId]) ? artistById[featuredId] : artists[0];
        if (!featured) return null;
        let disco;
        if (discoIds.length > 0) {
          disco = discoIds.map((id) => artistById[id]).filter(Boolean).filter((a) => a.id !== featured.id);
        } else {
          disco = artists.filter((a) => a.id !== featured.id).slice(0, 4);
        }
        // Strict per-language eyebrow lookup: if admin saved this field, only
        // show the value of the *current* language. Empty current-lang value
        // means "hide" — the red line disappears too. If nothing is saved
        // yet, fall back to the i18n default so existing sites keep showing
        // "FEATURED ARTIST" / "SEÇİLMİŞ SƏNƏTÇİ" out of the box.
        const savedEyebrow = content?.pages?.home?.featured_artist_eyebrow;
        const featuredEyebrow = (savedEyebrow && typeof savedEyebrow === "object")
          ? (savedEyebrow[lang] ?? "")
          : (t.sections.featuredArtist || "");
        return (
          <section className="max-w-screen-2xl mx-auto px-5 md:px-12 pb-24 md:pb-32 pt-20 md:pt-28">
            <div className="grid md:grid-cols-12 gap-8 md:gap-12 items-center">
              <div className="md:col-span-5 zoom-wrap aspect-[3/4] card-flat overflow-hidden">
                <img src={featured.image_url} alt={featured.name} className="w-full h-full object-cover" />
              </div>
              <div className="md:col-span-7">
                {featuredEyebrow && (
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-px bg-[var(--accent)]"/>
                    <span className="section-eyebrow">{featuredEyebrow}</span>
                  </div>
                )}
                {featured.name && (
                  <h2 className="section-title text-4xl md:text-5xl lg:text-[56px] leading-[1.04]">{featured.name}</h2>
                )}
                <p className="text-[var(--text-2)] max-w-xl mt-6 leading-relaxed text-[15px] font-light">{featured.bio}</p>
                {disco.length > 0 && (
                  <div className="flex flex-wrap gap-x-6 gap-y-2 mt-7 metadata text-[var(--text-muted)]">
                    <span>DİSKOQRAFIYA</span>
                    {disco.slice(0, 4).map((a) => (
                      <Link key={a.id} to={`/artist/${a.slug}`} className="hover:text-[var(--primary)]">{a.name}</Link>
                    ))}
                  </div>
                )}
                <Link to={`/artist/${featured.slug}`} className="btn-outline mt-9" data-testid="featured-artist-cta">
                  <Play size={12} /> {t.sections.exploreDiscography}
                </Link>
              </div>
            </div>
          </section>
        );
      })()}

      {/* ============== JOURNAL PREVIEW (only if admin published posts) ============== */}
      {journal.length > 0 && (
      <section className="border-t border-[var(--border-soft)] pt-20 md:pt-28 pb-20 md:pb-28">
        <div className="max-w-screen-2xl mx-auto px-5 md:px-12">
          <div className="grid md:grid-cols-12 gap-8 md:gap-16 items-end mb-14 md:mb-20">
            <div className="md:col-span-7">
              {(() => {
                // Strict per-language eyebrow: hidden when the current lang is empty.
                const savedJ = content?.pages?.home?.journal_eyebrow;
                const jEyebrow = (savedJ && typeof savedJ === "object")
                  ? (savedJ[lang] ?? "")
                  : "JURNAL · ISSUE 47";
                if (!jEyebrow) return null;
                return <div className="section-eyebrow mb-5">{jEyebrow}</div>;
              })()}
              {tp("home", "journal_title", "Oxumalı.") && (
                <h2 className="section-title text-4xl md:text-5xl lg:text-[60px] leading-[1.04]">
                  {tp("home", "journal_title", "Oxumalı.")}
                </h2>
              )}
            </div>
            <div className="md:col-span-5 flex md:justify-end items-center gap-6">
              <p className="text-[var(--text-muted)] metadata max-w-xs hidden md:block">
                {tp("home", "journal_lede", "Vinil mədəniyyəti, audiofil texnika və yerli səhnədən hekayələr.")}
              </p>
              <Link to="/blog" className="metadata draw-underline hover:text-[var(--accent)] inline-flex items-center gap-2 flex-shrink-0" data-testid="journal-all">
                Hamısına bax <ArrowRight size={12}/>
              </Link>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-x-8 gap-y-14">
            {journal.map((p, i) => {
              const title = pick(p.title);
              const eyebrow = pick(p.eyebrow);
              const cover = p.cover_url || p.cover;
              const dateStr = p.date ? new Date(p.date).toLocaleDateString() : "";
              return (
              <Link key={p.id || p.slug} to={`/blog/${p.slug}`} className="group block" data-testid={`journal-${p.slug}`}>
                <div className="zoom-wrap aspect-[4/3] overflow-hidden mb-5 relative">
                  {cover
                    ? <img src={cover} alt={title} className="w-full h-full object-cover"/>
                    : <div className="w-full h-full bg-[var(--surface)]" />}
                  <div className="absolute top-4 left-4 metadata bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-full text-[var(--text)]">
                    № {String(i + 1).padStart(2, '0')}
                  </div>
                </div>
                <div className="metadata text-[var(--text-muted)] flex items-center gap-2">
                  {eyebrow && <span>{eyebrow}</span>}
                  {eyebrow && dateStr && <span>·</span>}
                  {dateStr && <span>{dateStr}</span>}
                </div>
                <h3 className="section-title text-xl md:text-2xl mt-3 group-hover:text-[var(--accent)] transition-colors leading-tight">{title}</h3>
                <span className="metadata draw-underline mt-4 inline-flex items-center gap-2 group-hover:text-[var(--accent)]">
                  Oxu <ArrowRight size={12}/>
                </span>
              </Link>
              );
            })}
          </div>
        </div>
      </section>
      )}

      {/* ============== NEWSLETTER / STAY IN TUNE (removed) ============== */}
    </div>
  );
}
